import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import { useEffect, useCallback } from "react";
import type { Database } from "../types/supabase";

type Store = Database["public"]["Tables"]["stores"]["Row"];
type Product = Database["public"]["Tables"]["products"]["Row"];
type MarketingActivityType =
  Database["public"]["Tables"]["marketing_activity_types"]["Row"];
type MarketingActivity =
  Database["public"]["Tables"]["marketing_activities"]["Row"] & {
    product?: Product | null;
    marketing_activity_type?: MarketingActivityType | null;
  };
type StoreActivity = Database["public"]["Tables"]["store_activities"]["Row"] & {
  store?: Store | null;
  marketing_activity?: MarketingActivity | null;
};
type ProofTarget = Database["public"]["Enums"]["proof_target"];
type User = Database["public"]["Tables"]["users"]["Row"];

type ProductRequirementInfo = {
  id: number;
  code: string;
  description: string;
  labels: Record<string, string>;
  order: number;
};

// Pending proof from the new proofs model
export type PendingProof = Database["public"]["Tables"]["proofs"]["Row"] & {
  target: ProofTarget;
  // For store proofs
  store_proof?: {
    store_id: number;
    store?: Store | null;
  } | null;
  // For product proofs
  product_proof?: {
    store_activity_id: number;
    product_requirement_id: number;
    product_requirement?: ProductRequirementInfo | null;
    store_activity?: StoreActivity | null;
  } | null;
  // Rep who took the photo (fetched separately)
  rep_user?: User | null;
};

const proofsToApproveAtom = atom<PendingProof[]>([]);

export function useProofsToApprove() {
  const [proofs, setProofs] = useAtom(proofsToApproveAtom);

  const fetchProofs = useCallback(async () => {
    try {
      // Fetch pending proofs with their extension data
      const { error, data } = await supabase
        .from("proofs")
        .select(
          `
          *,
          store_proof:store_proofs(
            store_id,
            store:stores(*)
          ),
          product_proof:product_proofs(
            store_activity_id,
            product_requirement_id,
            product_requirement:product_requirements(
              id,
              code,
              description,
              labels,
              "order"
            ),
            store_activity:store_activities(
              *,
              store:stores(*),
              marketing_activity:marketing_activities(
                *,
                marketing_activity_type:marketing_activity_types(*),
                product:products(*)
              )
            )
          )
        `,
        )
        .eq("status", "pending")
        .order("created_at", { ascending: true }); // FIFO: oldest first

      if (error) {
        console.error("Error fetching proofs to approve:", error);
        throw error;
      }

      // Transform the data to flatten nested arrays from joins
      const transformedData = (data || []).map((proof) => {
        const storeProof = Array.isArray(proof.store_proof)
          ? proof.store_proof[0] || null
          : proof.store_proof;
        const rawProductProof = Array.isArray(proof.product_proof)
          ? proof.product_proof[0] || null
          : proof.product_proof;

        // Flatten the product_requirement join (may come as array from PostgREST)
        const productProof = rawProductProof
          ? {
              ...rawProductProof,
              product_requirement: Array.isArray(
                rawProductProof.product_requirement,
              )
                ? rawProductProof.product_requirement[0] || null
                : rawProductProof.product_requirement,
            }
          : null;

        return {
          ...proof,
          store_proof: storeProof,
          product_proof: productProof,
        };
      });

      // Fetch user details for each proof (proofs.user_id -> public.users)
      const userIds = [
        ...new Set(
          transformedData
            .map((p) => p.user_id)
            .filter((id): id is string => id != null),
        ),
      ];

      let usersMap: Map<string, User> = new Map();
      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from("users")
          .select("*")
          .in("id", userIds);

        if (usersData) {
          usersMap = new Map(usersData.map((u) => [u.id, u]));
        }
      }

      // Attach user data to proofs
      const proofsWithUsers = transformedData.map((proof) => ({
        ...proof,
        rep_user: proof.user_id ? (usersMap.get(proof.user_id) ?? null) : null,
      })) as PendingProof[];

      setProofs(proofsWithUsers);
    } catch (e) {
      console.error("Failed to fetch proofs:", e);
    }
  }, [setProofs]);

  useEffect(() => {
    fetchProofs();
  }, [fetchProofs]);

  return [proofs, setProofs, fetchProofs] as const;
}

export async function fetchProofById(
  proofId: number,
): Promise<PendingProof | null> {
  const { data, error } = await supabase
    .from("proofs")
    .select(
      `
      *,
      store_proof:store_proofs(
        store_id,
        store:stores(*)
      ),
      product_proof:product_proofs(
        store_activity_id,
        product_requirement_id,
        product_requirement:product_requirements(
          id,
          code,
          description,
          labels,
          "order"
        ),
        store_activity:store_activities(
          *,
          store:stores(*),
          marketing_activity:marketing_activities(
            *,
            marketing_activity_type:marketing_activity_types(*),
            product:products(*)
          )
        )
      )
    `,
    )
    .eq("id", proofId)
    .single();

  if (error || !data) {
    console.error("Error fetching proof by ID:", error);
    return null;
  }

  const storeProof = Array.isArray(data.store_proof)
    ? data.store_proof[0] || null
    : data.store_proof;
  const rawProductProof = Array.isArray(data.product_proof)
    ? data.product_proof[0] || null
    : data.product_proof;

  const productProof = rawProductProof
    ? {
        ...rawProductProof,
        product_requirement: Array.isArray(rawProductProof.product_requirement)
          ? rawProductProof.product_requirement[0] || null
          : rawProductProof.product_requirement,
      }
    : null;

  const proof = {
    ...data,
    store_proof: storeProof,
    product_proof: productProof,
  };

  // Fetch user
  let rep_user: User | null = null;
  if (proof.user_id) {
    const { data: userData } = await supabase
      .from("users")
      .select("*")
      .eq("id", proof.user_id)
      .single();
    rep_user = userData ?? null;
  }

  return { ...proof, rep_user } as PendingProof;
}
